import { cn } from '@/lib/utils'

function ToggleTrack({ checked }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
        checked ? 'bg-primary' : 'bg-muted-foreground/30',
      )}
    >
      <span className={cn('inline-block h-4 w-4 rounded-full bg-background shadow transition-transform', checked ? 'translate-x-4' : 'translate-x-0.5')} />
    </span>
  )
}

// Label-free switch for places where the surrounding heading already names
// the setting. The explicit accessible label keeps the control clear to screen
// readers without adding another visible line of copy.
export function CompactToggle({ label, checked, onCheckedChange, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className="inline-flex shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <ToggleTrack checked={checked} />
    </button>
  )
}

// The single on/off toggle used across the app – plan settings (tiered match,
// nonelective, profit sharing, vesting, eligibility, after-tax, termination) and
// the report customizer – so every toggle looks and behaves identically. The whole
// labeled row is the click / keyboard target; the pill on the right shows state.
export function ToggleSetting({ title, description, checked, onCheckedChange, strong = false, disabled = false }) {
  const handleToggle = () => {
    if (!disabled) onCheckedChange(!checked)
  }
  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    handleToggle()
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      aria-pressed={checked}
      aria-disabled={disabled || undefined}
      className={cn(
        'flex w-full items-start justify-between gap-4 rounded-md py-1 text-left transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <div className="min-w-0 space-y-0.5">
        <span className={cn('block text-sm text-foreground', strong && 'font-medium')}>{title}</span>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      <ToggleTrack checked={checked} />
    </div>
  )
}

export default ToggleSetting
